import { useCallback, useRef, useState } from 'react'

import isBrowser from '@src/quince-site-common/src/utils/isBrowser'

// Must match the `extended` SelectColorGroup variant (@lastbrand/web-ui): 32px
// border-box swatch, `space-12` gap between swatches.
const SWATCH_SIZE = 32
const SWATCH_GAP = 12
const SWATCH_SLOT = SWATCH_SIZE + SWATCH_GAP

/**
 * How many swatches the mini-PDP color row may show without wrapping, reserving
 * one full swatch slot (44px) for the trailing "+N" control (8px column gap +
 * ~24px body-14 text for two digits ≈ 32px — the 44px slot is deliberate headroom).
 *
 * Returns `maxFit - 1` rather than `maxFit` deliberately: `useMiniPdpOptionList`
 * shows the full list whenever the excess is ≤ 1, so with limit `maxFit - 1`
 * either all values fit on the line (excess ≤ 1 → up to `maxFit` swatches, no
 * "+N") or `maxFit - 1` swatches render with "+N" in the freed last slot.
 */
export function computeMiniPdpSwatchLimit(availableWidth: number, fallback: number): number {
  if (availableWidth <= 0) return fallback

  const maxFit = Math.floor((availableWidth + SWATCH_GAP) / SWATCH_SLOT)
  return Math.max(1, maxFit - 1)
}

/** Measures the attached row and derives the swatch `optionLimit` from its width. */
export function useMiniPdpSwatchLimit(fallback: number): [(node: HTMLElement | null) => void, number] {
  const [width, setWidth] = useState(0)
  const observerRef = useRef<ResizeObserver | null>(null)

  const measureRef = useCallback((node: HTMLElement | null) => {
    observerRef.current?.disconnect()
    observerRef.current = null

    if (!isBrowser() || !node || typeof ResizeObserver === 'undefined') return

    const update = () => {
      setWidth(node.getBoundingClientRect().width)
    }
    update()

    const observer = new ResizeObserver(update)
    observer.observe(node)
    observerRef.current = observer
  }, [])

  return [measureRef, computeMiniPdpSwatchLimit(width, fallback)]
}
